import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { BusinessSettingData } from '@/types/api';

const KEY = ['settings', 'business'] as const;

export function useBusinessSettings() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<BusinessSettingData> => {
      const { data } = await api.get<BusinessSettingData>('/v1/settings/business');
      return data;
    },
  });
}

export function useUpdateBusinessSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<BusinessSettingData>): Promise<BusinessSettingData> => {
      await primeCsrf();
      const { data } = await api.patch<BusinessSettingData>('/v1/settings/business', patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** The two slots the inbound call flow plays audio from. */
export type VoiceClipSlot = 'voicemail_greeting' | 'screening_prompt';

export function useUploadVoiceClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ slot, file }: { slot: VoiceClipSlot; file: File }): Promise<void> => {
      await primeCsrf();
      const form = new FormData();
      form.append('clip', file);
      await api.post(`/v1/settings/business/voice-clips/${slot}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteVoiceClip() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slot: VoiceClipSlot): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/settings/business/voice-clips/${slot}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
